import { getSupportedAppType } from "src/modules/apps/pages/create-app-page-templates/services";
import { createAppUtils } from "src/modules/apps/pages/create-app-page-templates/create-app-page-utils";

// Case-insensitive match of `name` against an adapter's identifiers.
const findMatchingComponent = (adapter: any, name: string): boolean => {
    const normalized = name.toLowerCase();
    return [adapter?.adapter, adapter?.componentName, adapter?.adapterName].some(
        (value: string) => value?.toLowerCase() === normalized
    );
};

// Throws if the source adapter is not enabled for the current license. The `isEnabled` flag is
// resolved inside createAppUtils.getSources() via the adapter license check.
// Returns silently when the source cannot be resolved — an unresolvable adapter is not a
// license failure, so let the normal selection flow surface it rather than blocking here.
export const checkIfSourceEnabled = (source: string) => {
    if (!source) return;
    const sourceAdapter = createAppUtils.getSources().find((s: any) => findMatchingComponent(s, source));
    if (!sourceAdapter) return;
    if (!sourceAdapter.isEnabled) throw `The adapter ${sourceAdapter.id} is not enabled for your license`;
};

// Mirrors checkIfSourceEnabled for the target side. getTargets requires a truthy `source` arg to
// return its list but does not filter by it, so "*" is passed to get every target's license state.
export const checkIfTargetEnabled = (target: string) => {
    if (!target) return;
    const targetAdapter = createAppUtils.getTargets("*").find((t: any) => findMatchingComponent(t, target));
    if (!targetAdapter) return;
    if (!targetAdapter.isEnabled) throw `The adapter ${targetAdapter.id} is not enabled for your license`;
};

// Resolves a source/target pair (by componentName) to the app types Striim supports for it.
// Returns an array of { label, value } — value is the app type (e.g. AUTOMATEDILCDC, CDC, IL).
export const resolveSupportedAppTypes = (source: string, target?: string) => {
    checkIfSourceEnabled(source);

    const sourceAdapter = createAppUtils.getSources().find((s: any) => s.adapter === source);
    if (!sourceAdapter) return [];

    const targetAdapter = { adapter: target };
    return getSupportedAppType(sourceAdapter as any, targetAdapter as any) ?? [];
};
